import { FC, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useSubsidiariesApi } from "../../http/subsidiaries/api";
import { getLanguages, Subsidiary } from "../../http/subsidiaries/types";
import { PaginationQueryWithFields } from "../../types";
import { isWithinLast48h } from "../../types/utils";
import SvgAdd from "../icons/Add";
import Button from "../ui/Button";
import Header from "../ui/Header";
import Panel from "../ui/Panel";
import Table from "../ui/Table";
import BooleanColumnFilter from "../ui/columnFilters/BooleanColumnFilter";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";

import { useUserSettings } from "../../context/userSettingsContext";
import SvgCancel from "../icons/Cancel";
import SvgCheckCircle from "../icons/CheckCircle";
import { Check } from "../ui/Check";
import { Action, getDeleteAction, getEditAction, getHistoryAction } from "../ui/ListItemMenu";
import NewItemIcon from "../ui/NewItemIcon";
import DeleteSubsidiaryDialog from "./DeleteSubsidiaryDialog";
import SubsidiaryHistoryDialog from "./SubsidiaryHistoryDialog";

const SUBSIDIARIES_FIELDS = [
  "id",
  "createdAt",
  "name",
  "allowMultiPlates",
  "countryIsoCodeAlpha2",
  "vatDeductionCode",
  "vatServiceCode",
  "defaultLanguage",
  "semiramisOrganizationCode",
  "versionNumber",
] as const;
type SubsidiaryField = (typeof SUBSIDIARIES_FIELDS)[number];
type SubsidiarySearch = Pick<Subsidiary, SubsidiaryField>;

const Subsidiaries: FC = () => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const subsidiariesApi = useSubsidiariesApi();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const [itemToDelete, setItemToDelete] = useState<SubsidiarySearch>();
  const [itemToShowVersions, setItemToShowVersions] = useState<SubsidiarySearch>();
  const navigate = useNavigate();

  const navToEdit = (item: SubsidiarySearch) => {
    navigate(`/admin/subsidiaries/${item.id}`);
  };

  const query: PaginationQueryWithFields<SubsidiarySearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: SUBSIDIARIES_FIELDS,
  };

  const result = subsidiariesApi.useAdminSubsidiaries(
    query,
    aborter.current.signal,
    userSettings.permissions.subsidiaries.read,
  );
  const [data] = useDataInitializer(result.data, result.error);

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const filterOptions = {
    get: subsidiariesApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const languages = getLanguages(i18n.t);

  const getRowActions = (item: SubsidiarySearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.subsidiaries.update) {
      actions.push(getEditAction(i18n, () => navToEdit(item)));
    }

    actions.push(getHistoryAction(i18n, () => setItemToShowVersions(item)));

    if (userSettings.permissions.subsidiaries.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  return (
    <>
      <Header title={i18n.t.subsidiaries.plural} />
      <Panel>
        <Table<SubsidiarySearch>
          filters={filters}
          searchOptions={{
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          buttons={{
            main: !!userSettings.permissions.subsidiaries.insert && (
              <Button buttonProps={{ onClick: () => navigate("/admin/subsidiaries/new") }} glyph={SvgAdd} />
            ),
          }}
          head={
            <>
              <Table.Th title={i18n.t.common.name} fieldName="subsidiary.name" order={orderOptions} />
              <Table.Th
                title={i18n.t.common.allowMultiplePlates}
                fieldName="subsidiary.allowMultiPlates"
                filters={filterOptions}
                filter={BooleanColumnFilter}
              />
              <Table.Th
                title={i18n.t.common.countryIsoCode}
                fieldName="subsidiary.countryIsoCode"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <th align="left">{i18n.t.common.vatDeductionCode}</th>
              <th align="left">{i18n.t.common.vatServiceCode}</th>
              <Table.Th
                title={i18n.t.common.defaultLanguage}
                fieldName="subsidiary.defaultLanguage"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
                formatter={(value) => languages.find((x) => x.value === value)?.label}
              />
              <Table.Th
                title={i18n.t.common.semiramisOrganizationCode}
                fieldName="subsidiary.semiramis.code"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <th></th>
            </>
          }
          onRowClick={(item) => {
            navToEdit(item);
          }}
          renderRow={(item) => (
            <>
              <td>
                {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                {item.name}
              </td>
              <td>
                {
                  <Check
                    icon={item.allowMultiPlates ? SvgCheckCircle : SvgCancel}
                    color={item.allowMultiPlates ? "green" : "red"}
                    obscured={!item.allowMultiPlates}
                  />
                }
              </td>
              <td>{item.countryIsoCodeAlpha2}</td>
              <td>{item.vatDeductionCode}</td>
              <td>{item.vatServiceCode}</td>
              <td>{item.defaultLanguage && languages.find((x) => x.value == item.defaultLanguage)?.label}</td>
              <td>{item.semiramisOrganizationCode}</td>
            </>
          )}
          rowActions={getRowActions}
          error={result.error}
          items={data?.items}
          total={data?.total}
        />
      </Panel>

      {!!itemToDelete && (
        <DeleteSubsidiaryDialog subsidiaryId={itemToDelete.id} onClose={() => setItemToDelete(undefined)} />
      )}

      {!!itemToShowVersions && (
        <SubsidiaryHistoryDialog
          itemToShowVersionsId={itemToShowVersions.id}
          itemVersionToShowVersions={itemToShowVersions.versionNumber}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}
    </>
  );
};

export default Subsidiaries;
